/// <amd-module name="Wit/Common/WorkItemFieldService/IWorkItemFieldService2" />
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { IWorkItemDataManager } from "Wit/WorkItemDataManager/WorkItemDataManager";

export interface IFieldAllowedValuesResponse {
    data?: string[];
    errorMessage?: string;
}

export interface IFieldResponse {
    data?: IFieldEntry[];
    errorMessage?: string;
}

export interface IFieldAllowedValuesRequest {
    fieldRefName: string;
    projectId?: string;
    workItemTypeName?: string;
}

/**
 * This is a new version of the IWorkItemFieldService that uses the metadata cache instead of loading fields from the fields-data-provider.
 * Using this version should improve performance because it uses the indexdb metadatacache to avoid making calls to the server whenever possible
 * 
 *  The goal is to eventually replace the IWorkItemFieldService with this version at which point we can rename this version to IWorkItemFieldService.
 *  Once we've replaced all usages, this version can be renamed to IWorkItemFieldService and the old version can be removed.
 * 
 *  Future potential optimization:
 *    Scopes fields to only those within the current project (using beginGetFieldProjectData instead of beginGetFields)
 *    This would reduce the amount of data significantly since it would only load fields for the current project
 *       and would exclude WEF fields
 */
export interface IWorkItemFieldService2 extends IVssService {
    /**
     * Get allowed values for a field, optionally specifying a project and work item type
     */
    getAllowedValues(fieldRefName: string, projectId?: string, workItemTypeName?: string): Promise<string[] | undefined>;
    /**
     * Get a field object from a field id
     */
    getField(id: number): IFieldEntry | undefined;
    /**
     * Get a field object from a field reference name
     */
    getFieldByReferenceName(referenceName: string): IFieldEntry | undefined;

    /**
     * Load fields
     */
    loadFields(): Promise<IFieldEntry[] | undefined>;
    /**
     * Get the error message if no data is returned for fields
     */
    getFieldsErrorMessage(): string | undefined;
    /**
     * Get the error message if no data is returned for allowed values
     */
    getFieldAllowedValuesErrorMessage(): string | undefined;
}

class WorkItemFieldService2 extends VssService implements IWorkItemFieldService2 {
    private allowedValuesCache: { [key: string]: Promise<string[] | undefined> } = {};
    private fields: IFieldEntry[] | undefined;
    private fieldsErrorMessage : string | undefined;
    private fieldsById: Map<number, IFieldEntry> | undefined;
    private fieldsByReferenceName: Map<string, IFieldEntry> | undefined;
    private allowedValuesDataProviderError: string | undefined;
    private isLoadingPromise : Promise<IFieldEntry[] | undefined> | undefined;

    public getField(id: number): IFieldEntry | undefined {
        if ($DEBUG && !this.fieldsById) {
            console.warn("Fields have not been loaded. Call loadFields() first.");
        }
        return this.fieldsById?.get(id);
    }

    public getFieldByReferenceName(referenceName: string): IFieldEntry | undefined {
        if ($DEBUG && !this.fieldsByReferenceName) {
            console.warn("Fields have not been loaded. Call loadFields() first.");
        }
        return this.fieldsByReferenceName?.get(referenceName);
    }

    public async loadFields(): Promise<IFieldEntry[] | undefined> {
        if (this.isLoadingPromise) {
            return this.isLoadingPromise;
        }
        if (!this.fields) {
            this.isLoadingPromise = this.innerLoadFields();
            await this.isLoadingPromise;
        }

        return this.fields;
    }

    private async innerLoadFields() : Promise<IFieldEntry[] | undefined> {
        const workItemDataManager = this.pageContext.getService<IWorkItemDataManager>("IWorkItemDataManager");
        try {
            const fieldsData = await workItemDataManager.beginGetFields();
            this.loadFieldMaps(fieldsData);
            return this.fields;
        } catch (e) {
            // The underlying WorkItemStoreLevelWITDataSource that makes the API call throws an exception if the data provider is not found
            // The exception is of type string which contains the error message.
            if (typeof e === "string" && e.length > 0) {
                this.fieldsErrorMessage = e;
            }
        }
    }

    public async getAllowedValues(fieldRefName: string, projectId?: string, workItemTypeName?: string): Promise<string[] | undefined> {
        const cacheKey = getCacheKey(projectId, fieldRefName, workItemTypeName);
        let promise: Promise<string[] | undefined> | undefined;
        if (!this.allowedValuesCache[cacheKey]) {
            // check for pseudo tag fields (System.Tag-Add and System.Tag-Remove)
            if (fieldRefName.startsWith(CoreFieldRefNames.Tags)) {
                promise = projectId ? this.getTagsSuggestedValues(projectId).then(data => data ?? new Array<string>()) : Promise.resolve([]);
            } else {
                const requestParams = {
                    projectId,
                    fieldRefName,
                    workItemTypeName
                };
                promise = this.loadAllowedValues(requestParams);
            }
            this.allowedValuesCache[cacheKey] = promise;
        }

        return promise || this.allowedValuesCache[cacheKey];
    }

    public getFieldsErrorMessage(): string | undefined {
        if (this.fieldsErrorMessage) {
            return this.fieldsErrorMessage;
        }
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const exception = contributionService.getDataProviderExceptions()[FieldDataProviders.FieldsDataProvider];
        return exception?.message;
    }

    public getFieldAllowedValuesErrorMessage(): string | undefined {
        if (this.allowedValuesDataProviderError) {
            return this.allowedValuesDataProviderError;
        }

        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const exception = contributionService.getDataProviderExceptions()[FieldDataProviders.FieldAllowedValuesDataProvider];
        return exception?.message;
    }

    private getTagsSuggestedValues(projectId: string): Promise<string[] | undefined> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const requestParams = {
            projectScope: projectId
        };
        return contributionService.getDataAsync<string[]>(
            FieldDataProviders.WorkItemTagSuggestionsDataProvider,
            requestParams,
            /* ignoreCache */ true
        );
    }

    private async loadAllowedValues(requestParams: IFieldAllowedValuesRequest): Promise<string[] | undefined> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const response = await contributionService.getDataAsync<IFieldAllowedValuesResponse>(
            FieldDataProviders.FieldAllowedValuesDataProvider,
            requestParams,
            /* ignoreCache */ true
        );

        if (response?.data) {
            return response.data;
        } else if (response?.errorMessage) {
            this.allowedValuesDataProviderError = response.errorMessage;
        }
    }

    private loadFieldMaps(fieldsData?: IFieldEntry[] | null) {
        if (fieldsData && fieldsData.length > 0) {
            this.fieldsById = new Map<number, IFieldEntry>();
            this.fieldsByReferenceName = new Map<string, IFieldEntry>();
            this.fields = [...fieldsData];
            this.fields.forEach(field => {
                this.fieldsById?.set(field.id, field);
                this.fieldsByReferenceName?.set(field.referenceName, field);
            });
        }
    }
}

const serviceName = "IWorkItemFieldService2";
Services.add(serviceName, { serviceFactory: WorkItemFieldService2 });

export function getWorkItemFieldService(pageContext: IVssPageContext): IWorkItemFieldService2 {
    return pageContext.getService<IWorkItemFieldService2>(serviceName);
}

const enum FieldDataProviders {
    FieldAllowedValuesDataProvider = "ms.vss-work-web.fields-allowed-values-provider",
    FieldsDataProvider = "ms.vss-work-web.fields-data-provider",
    WorkItemTagSuggestionsDataProvider = "ms.vss-work-web.work-item-tag-suggestions-data-provider"
}

function getCacheKey(projectId?: string, fieldName?: string, workItemTypeName?: string): string {
    if (projectId || fieldName || workItemTypeName) {
        return [projectId, fieldName, workItemTypeName].filter(v => v).join("-");
    } else {
        return "global";
    }
}
